import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

/**
 * Decodifica un JWT para extraer el payload (sin verificar firma)
 */
function decodeToken(token) {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

/**
 * Hook para manejar el tiempo de sesión y renovación automática de tokens.
 * Solo renueva si hubo actividad reciente del usuario.
 * 
 * @param {Object} options
 * @param {Function} options.onLogout - Callback cuando la sesión expira
 * @param {number} options.refreshBeforeSeconds - Segundos antes de expirar para renovar (default: 300 = 5min)
 * @param {number} options.inactivityThresholdSeconds - Segundos de inactividad permitidos para renovar (default: 900 = 15min)
 * @returns {{ remainingSeconds: number, formattedTime: string, refresh: Function, isActive: boolean }}
 */
export function useSessionTimer({ onLogout, refreshBeforeSeconds = 300, inactivityThresholdSeconds = 900 } = {}) {
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const refreshAttemptedRef = useRef(false);
  const intervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const getTokenExpiry = useCallback(() => {
    const token = localStorage.getItem("agent_token");
    if (!token) return null;
    
    const decoded = decodeToken(token);
    if (!decoded?.exp) return null;
    
    return decoded.exp;
  }, []);

  const calculateRemaining = useCallback(() => {
    const exp = getTokenExpiry();
    if (!exp) return null;
    
    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, exp - now);
  }, [getTokenExpiry]);

  const checkActivity = useCallback(() => {
    const inactiveSeconds = (Date.now() - lastActivityRef.current) / 1000;
    const active = inactiveSeconds < inactivityThresholdSeconds;
    setIsActive(active);
    return active;
  }, [inactivityThresholdSeconds]);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsActive(true);
  }, []);

  const refresh = useCallback(async () => {
    if (isRefreshing) return false;
    
    const token = localStorage.getItem("agent_token");
    if (!token) return false;
    
    try {
      setIsRefreshing(true);
      refreshAttemptedRef.current = true;
      
      const { data } = await axios.post("/auth/refresh", {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (data.token) {
        localStorage.setItem("agent_token", data.token);
        axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
        refreshAttemptedRef.current = false;
        console.log("🔄 Token renovado exitosamente");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error renovando token:", error.message);
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  // Detectar actividad del usuario
  useEffect(() => {
    const events = ["mousedown", "keydown", "touchstart", "scroll", "mousemove"];
    
    // Throttle para no actualizar en cada movimiento de mouse
    let throttleTimer = null;
    const throttledUpdate = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        updateActivity();
        throttleTimer = null;
      }, 1000);
    };

    events.forEach(event => {
      window.addEventListener(event, throttledUpdate, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledUpdate);
      });
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [updateActivity]);

  useEffect(() => {
    const updateRemaining = async () => {
      const remaining = calculateRemaining();
      
      if (remaining === null) {
        setRemainingSeconds(null);
        return;
      }
      
      setRemainingSeconds(remaining);
      
      // Verificar actividad reciente
      const userIsActive = checkActivity();
      
      // Renovar automáticamente solo si:
      // 1. Quedan menos de refreshBeforeSeconds
      // 2. El usuario estuvo activo recientemente
      // 3. No se intentó renovar ya
      if (remaining > 0 && remaining <= refreshBeforeSeconds && userIsActive && !refreshAttemptedRef.current) {
        const success = await refresh();
        if (success) {
          setRemainingSeconds(calculateRemaining());
        }
      }
      
      // Sesión expirada
      if (remaining <= 0) {
        if (onLogout) {
          onLogout();
        }
      }
    };

    // Actualizar inmediatamente
    updateRemaining();

    // Actualizar cada segundo
    intervalRef.current = setInterval(updateRemaining, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [calculateRemaining, refresh, refreshBeforeSeconds, onLogout, checkActivity]);

  // Formatear tiempo restante como HH:MM:SS
  const formattedTime = remainingSeconds !== null
    ? formatTime(remainingSeconds)
    : "--:--:--";

  return {
    remainingSeconds,
    formattedTime,
    refresh,
    isRefreshing,
    isActive
  };
}

function formatTime(totalSeconds) {
  if (totalSeconds <= 0) return "00:00:00";
  
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  return [hours, minutes, seconds]
    .map(v => String(v).padStart(2, "0"))
    .join(":");
}
